/* eslint-disable */
// All CAPI traffic goes through /api/meta-capi (server-side proxy).
// The access token lives only in the META_CAPI_ACCESS_TOKEN env var on Vercel.
const CAPI_PROXY_URL = "/checkout/api/meta-capi";

// ── Helpers ──────────────────────────────────────────────────────────────────

export function generateEventId() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

async function sha256(value) {
  const normalized = value.trim().toLowerCase();
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(normalized)
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function getFbclid() {
  return new URLSearchParams(window.location.search).get("fbclid");
}

/**
 * Build an fbc value from a raw fbclid, for the case where the pixel hasn't
 * written the _fbc cookie yet (blocked, slow, or the event fires first).
 *
 * Meta's format is fb.<subdomainIndex>.<creationTime>.<fbclid> and creationTime
 * is in MILLISECONDS — the same value the pixel itself writes. This used to
 * send seconds, a 10-digit number Meta reads as a 1970 timestamp, so every
 * click id we reconstructed here was either discarded or matched at near-zero
 * weight. That is the strongest attribution signal there is, on paid traffic
 * only, which is exactly the traffic that has to be measured.
 */
function buildFbc(fbclid) {
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : null;
}

/**
 * Normalize a Pakistani mobile number to Meta's expected format: country code
 * + number, digits only, no +, no leading 0. Meta hashes what you send and
 * compares to what it has, and it does NOT reliably strip the leading 0 or add
 * the 92 for you — so "03000132773" hashed as-is never matches Meta's
 * "923000132773". This is the fix.
 *
 *   03000132773      -> 923000132773   (local, leading 0)
 *   3000132773       -> 923000132773   (10-digit, no 0)
 *   +92 300 0132773  -> 923000132773   (already country code)
 *   00923000132773   -> 923000132773   (intl 00 prefix)
 */
function normalizePhone(raw) {
  let d = (raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("92")) return d;
  if (d.startsWith("0")) return "92" + d.slice(1);
  if (d.length === 10 && d.startsWith("3")) return "92" + d;
  return d; // unknown shape: send digits as-is rather than mangle
}

/**
 * Stable per-visitor external_id. A xid handed over from the landing site
 * (?xid= on the checkout URL) wins, so Meta stitches the landing session to
 * this purchase as one identity; otherwise we generate and persist our own.
 * Opaque, non-PII, sent as-is (unhashed) and identically on every event.
 */
const XID_KEY = "sjs_xid";

function getExternalId() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("xid");
    if (fromUrl) {
      localStorage.setItem(XID_KEY, fromUrl);
      return fromUrl;
    }
    let id = localStorage.getItem(XID_KEY);
    if (!id) {
      id = `x.${Date.now()}.${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(XID_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

/** Public: this visitor's external_id, for handing across to sajawal.school. */
export function currentExternalId() {
  return getExternalId() || "";
}

function browserSignals() {
  const fbclid = getFbclid();
  return {
    fbp: getCookie("_fbp") || undefined,
    fbc: getCookie("_fbc") || (fbclid ? buildFbc(fbclid) : undefined),
    client_user_agent: navigator.userAgent,
    external_id: getExternalId() || undefined,
  };
}

// ── Core sender ───────────────────────────────────────────────────────────────

async function sendCapiEvent({ event_name, event_id, user_data = {}, custom_data }) {
  const signals = browserSignals();

  // Hash any PII fields present
  const hashed = { ...signals };
  if (user_data.email) hashed.em = await sha256(user_data.email);
  if (user_data.phone) {
    const normalizedPhone = normalizePhone(user_data.phone);
    if (normalizedPhone) hashed.ph = await sha256(normalizedPhone);
  }
  if (user_data.name) {
    const parts = user_data.name.trim().split(/\s+/);
    hashed.fn = await sha256(parts[0]);
    if (parts.length > 1) hashed.ln = await sha256(parts.slice(1).join(" "));
  }

  const event = {
    event_name,
    event_id,
    event_time: Math.floor(Date.now() / 1000),
    action_source: "website",
    event_source_url: window.location.href,
    user_data: hashed,
  };
  if (custom_data) event.custom_data = custom_data;

  try {
    const res = await fetch(CAPI_PROXY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events: [event] }),
    });
    if (!res.ok) console.warn("[CAPI]", await res.json().catch(() => res.status));
  } catch (err) {
    console.warn("[CAPI] fetch error", err);
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Mirrors the PageView pixel event fired in index.html (same event_id = deduplication). */
export function trackPageViewCapi() {
  const eventId = window._metaEventIds?.pageView || generateEventId();
  sendCapiEvent({ event_name: "PageView", event_id: eventId });
}

/**
 * InitiateCheckout — browser pixel + CAPI pair with the same event_id.
 * Fires at Complete Enrollment (details validated), NOT at page load: an
 * anonymous page-load IC matched at 2% email / <1% phone in Events Manager.
 * Fired here, with advanced matching attached, it matches like Lead (~98%).
 */
export function trackInitiateCheckout({ name, email, phone }) {
  const eventId = window._metaEventIds?.initiateCheckout || generateEventId();

  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    const userData = advancedMatching({ name, email, phone });
    PIXEL_IDS.forEach((id) => window.fbq("init", id, userData));
    window.fbq(
      "track",
      "InitiateCheckout",
      { currency: "PKR", value: 6700 },
      { eventID: eventId }
    );
  }

  sendCapiEvent({
    event_name: "InitiateCheckout",
    event_id: eventId,
    user_data: { name, email, phone },
    custom_data: { currency: "PKR", value: 6700 },
  });
}

/**
 * Purchase event — fired at the moment a payment screenshot is APPROVED.
 * Sends full hashed customer data (email, phone, name) via CAPI so Meta can
 * match the purchase back to the ad click. The deterministic eventId is also
 * passed to the thank-you page, whose browser Purchase uses the same id —
 * Meta deduplicates them into one purchase, and page refreshes can't
 * double-count.
 */
export function trackPurchase({ name, email, phone, amount, eventId }) {
  return sendCapiEvent({
    event_name: "Purchase",
    event_id: eventId,
    user_data: { name, email, phone },
    custom_data: { currency: "PKR", value: Number(amount) },
  });
}

// Both pixels initialized in public/index.html.
const PIXEL_IDS = ["1589268205635225", "868164127067032"];

/**
 * Browser-pixel Purchase, fired at the SAME moment as the CAPI Purchase above
 * and with the same event_id, so Meta deduplicates the pair into one sale.
 *
 * Previously the browser Purchase only fired on the thank-you page, after a
 * 2.5s modal and a cross-domain redirect. Two consequences showed up in Events
 * Manager: a third of purchases never produced a browser event at all, and
 * only ~33% of Purchase events carried customer data — because the thank-you
 * page is a different origin that receives just amount + eid, so there is no
 * one to match on. Lead, which fires in place on this page, reaches 98%.
 *
 * Advanced matching values are passed unhashed on purpose: the Meta pixel
 * SHA-256s these fields in the browser before they are sent. Phone goes
 * through the same normalizer as CAPI so both channels hash an identical
 * string (92XXXXXXXXXX) and actually match.
 */
/**
 * Advanced-matching payload for the browser pixel. Values are passed UNHASHED
 * on purpose — the Meta pixel SHA-256s these fields in the browser before they
 * leave. Phone runs through the same normalizer as CAPI so both channels hash
 * an identical string (92XXXXXXXXXX) and actually match each other.
 *
 * external_id rides along on every call so the browser half of an event pair
 * carries the same identity as the server half.
 */
function advancedMatching({ name, email, phone }) {
  const userData = {};

  const externalId = getExternalId();
  if (externalId) userData.external_id = externalId;

  if (email) userData.em = email.trim().toLowerCase();
  const normalizedPhone = normalizePhone(phone);
  if (normalizedPhone) userData.ph = normalizedPhone;

  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts[0]) userData.fn = parts[0].toLowerCase();
  if (parts.length > 1) userData.ln = parts.slice(1).join(" ").toLowerCase();

  return userData;
}

export function trackPurchaseBrowser({ name, email, phone, amount, eventId }) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;

  const userData = advancedMatching({ name, email, phone });

  try {
    // Re-init attaches the buyer's details to subsequent events on both pixels.
    PIXEL_IDS.forEach((id) => window.fbq("init", id, userData));
    window.fbq(
      "track",
      "Purchase",
      { currency: "PKR", value: Number(amount) },
      { eventID: eventId }
    );
  } catch (err) {
    console.warn("[Pixel] purchase failed", err);
  }
}

/**
 * Lead event — fires both browser pixel and CAPI with the same event_id.
 * Call this when the user clicks Complete Enrollment and passes validation.
 *
 * The browser half re-inits with the lead's details first: the CAPI half has
 * always carried hashed email/phone/name, but the pixel half was going out with
 * nothing but cookies, halving the match quality of every Lead pair.
 */
export function trackLead({ name, email, phone }) {
  const eventId = generateEventId();

  if (typeof window.fbq === "function") {
    const userData = advancedMatching({ name, email, phone });
    PIXEL_IDS.forEach((id) => window.fbq("init", id, userData));
    window.fbq("track", "Lead", { content_name: "Enrollment" }, { eventID: eventId });
  }

  sendCapiEvent({
    event_name: "Lead",
    event_id: eventId,
    user_data: { name, email, phone },
    custom_data: { content_name: "Enrollment" },
  });
}
